import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import { Clock, Calendar, ExternalLink } from "lucide-react";
import { axiosInstance } from "../../../utils/axios";
import { alpha } from "@mui/material/styles";

// Query to fetch a client's data
const GET_CLIENT = `
  query GetClient($id: ID!) {
    getClient(id: $id) {
      id
      name
      companyName
      email
      avatar
    }
  }
`;

// Query to fetch a user's data
const GET_USER = `
  query GetUser($id: ID!) {
    getUser(id: $id) {
      id
      name
      email
      avatar
      client {
        id
        name
        companyName
      }
    }
  }
`;

// New query to fetch public events by entity ID
const GET_PUBLIC_EVENTS = `
  query GetPublicEventsByEntityId($id: ID!) {
    getPublicEventsByEntityId(id: $id) {
      id
      title
      url
      description
      duration
      assignment
      advanced {
        offerSeats {
          enabled
          seats
        }
      }
    }
  }
`;

// New query to fetch public events by username
const GET_PUBLIC_EVENTS_BY_USERNAME = `
  query GetPublicEventsByUsername($username: String!) {
    getPublicEventsByUsername(username: $username) {
      id
      title
      url
      description
      duration
      assignment
      advanced {
        offerSeats {
          enabled
          seats
        }
      }
    }
  }
`;

function PublicEventsPreview() {
  const [entity, setEntity] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entityType, setEntityType] = useState(null); // "client" or "user"

  // Get ID or username from URL params or query parameters
  const { id, username } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const entityId =
    id || queryParams.get("userId") || queryParams.get("clientId");
  const entityUsername = username;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (!entityId && !entityUsername) {
          throw new Error("Entity ID or username is required");
        }

        // Try to identify the entity type and fetch its details
        let identifiedEntity = null;
        let identifiedEntityType = null;
        let actualEntityId = entityId;

        // If we have a username, use the username-based query
        if (entityUsername && !entityId) {
          console.log(`Fetching events by username: ${entityUsername}`);
          const eventTypesResponse = await axiosInstance.post("/graphql", {
            query: GET_PUBLIC_EVENTS_BY_USERNAME,
            variables: { username: entityUsername },
          });

          if (eventTypesResponse.data?.getPublicEventsByUsername) {
            const fetchedEvents =
              eventTypesResponse.data.getPublicEventsByUsername;
            setEvents(fetchedEvents);
            console.log(
              `Found ${fetchedEvents.length} events for username ${entityUsername}`
            );

            // Create a mock entity since we don't need full entity details
            setEntity({
              id: "username-" + entityUsername,
              name: entityUsername.replace(/-/g, " "),
              type: "user",
            });
            setEntityType("user");
            setLoading(false);
            return;
          } else {
            throw new Error("No events found for username");
          }
        }

        // First try as a client
        try {
          console.log(`Attempting to fetch client with ID: ${entityId}`);
          const clientResponse = await axiosInstance.post("/graphql", {
            query: GET_CLIENT,
            variables: { id: entityId },
          });

          const clientData = clientResponse.data?.getClient;
          if (clientData) {
            console.log(`Found client: ${clientData.name}`);
            identifiedEntity = {
              ...clientData,
              type: "client",
            };
            identifiedEntityType = "client";
          }
        } catch (clientError) {
          console.error("Error fetching client:", clientError);
        }

        // If client not found, try as a user
        if (!identifiedEntity) {
          try {
            console.log(`Attempting to fetch user with ID: ${entityId}`);
            const userResponse = await axiosInstance.post("/graphql", {
              query: GET_USER,
              variables: { id: entityId },
            });

            const userData = userResponse.data?.getUser;
            if (userData) {
              console.log(`Found user: ${userData.name}`);
              identifiedEntity = {
                ...userData,
                type: "user",
              };
              identifiedEntityType = "user";
            }
          } catch (userError) {
            console.error("Error fetching user:", userError);
          }
        }

        if (!identifiedEntity) {
          throw new Error("No user or client found with the provided ID");
        }

        setEntity(identifiedEntity);
        setEntityType(identifiedEntityType);

        // Fetch public events using our dedicated resolver
        try {
          const eventTypesResponse = await axiosInstance.post("/graphql", {
            query: GET_PUBLIC_EVENTS,
            variables: { id: entityId },
          });

          // Direct access to data as instructed (no nested data.data)
          const publicEvents =
            eventTypesResponse.data?.getPublicEventsByEntityId || [];

          console.log(`Retrieved ${publicEvents.length} public event types`);
          setEvents(publicEvents);
        } catch (eventError) {
          console.error("Error fetching events:", eventError);
          // Access error directly as instructed
          setError(eventError.response || "Error fetching event types");
        }
      } catch (error) {
        console.error("Error in data fetching:", error);
        setError(error.message || "An error occurred while fetching data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [entityId, entityUsername]);

  // Helper function to truncate text
  const truncateText = (text, maxLength = 100) => {
    if (!text) return "";

    // Create a temporary div to parse HTML content
    const temp = document.createElement("div");
    temp.innerHTML = text;

    // Get text content without HTML tags
    const plainText = temp.textContent || temp.innerText || "";

    // Limit to specified character count
    if (plainText.length <= maxLength) {
      return plainText;
    }

    // Return truncated text with ellipsis
    return plainText.substring(0, maxLength) + "...";
  };

  // Helper function to generate preview URL
  const getPreviewUrl = (eventType) => {
    // If we have entity name and it's a user, use new slug format
    if (entity?.name && entity.type === "user") {
      const username = entity.name.toLowerCase().replace(/\s+/g, "-");
      return `/preview/events/preview/${username}/${eventType.url}`;
    }
    // If it's a client, we could potentially use client name, but for now fall back
    // For clients or when we don't have proper entity info, use old format
    return `/preview/preview-event-type?eventTypeId=${eventType.id}`;
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="60vh"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!entity) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Entity not found or invalid ID</Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        py: 6,
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      {/* Entity Profile Section */}
      <Box
        sx={{
          borderRadius: 2,
          background: (theme) => alpha(theme.palette.primary.main, 0.05),
          p: 4,
          mb: 5,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        <Avatar
          alt={entity?.name || "Entity"}
          src={entity?.avatar}
          sx={{
            width: 100,
            height: 100,
            mr: { xs: 0, sm: 4 },
            mb: { xs: 2, sm: 0 },
            boxShadow: 2,
          }}
        />
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {entity?.name || "Entity"}
          </Typography>
          {entity?.companyName && (
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {entity.companyName}
            </Typography>
          )}
          <Chip
            icon={<Calendar size={16} />}
            label="Available for booking"
            color="primary"
            variant="outlined"
            size="small"
            sx={{ mt: 1 }}
          />
        </Box>
      </Box>

      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Public Events
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Event Types Grid */}
      <Grid container spacing={3}>
        {events.length > 0 ? (
          events.map((eventType) => (
            <Grid item xs={12} md={6} key={eventType.id}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  height: "100%",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                    borderColor: "primary.main",
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {eventType.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {truncateText(eventType.description, 120)}
                    </Typography>
                  </Box>

                  <Box sx={{ mt: "auto" }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Clock
                        size={16}
                        style={{ marginRight: 8, color: "#666" }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {eventType.duration} minutes
                      </Typography>
                    </Box>

                    {eventType.advanced?.offerSeats?.enabled && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Group event: Up to{" "}
                          {eventType.advanced.offerSeats.seats} participants
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <ExternalLink
                        size={16}
                        style={{ marginRight: 8, color: "#666" }}
                      />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {eventType.url}
                      </Typography>
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      size="large"
                      component="a"
                      href={getPreviewUrl(eventType)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        mt: 2,
                        borderRadius: 2,
                        boxShadow: 2,
                        textTransform: "none",
                        fontWeight: "bold",
                        "&:hover": {
                          color: "#fff",
                        },
                      }}
                    >
                      Book This Event
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                py: 2,
              }}
            >
              No event types available for this {entityType || "entity"}.
            </Alert>
          </Grid>
        )}
      </Grid>
    </Container>
  );
}

export default PublicEventsPreview;
